import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import UI_EN from "./ui-en.json";
import { DEFAULT_LANG, localizePath, setCurrentLang, splitLangPath } from "./core";
import { api } from "../lib/api";

const I18nContext = createContext(null);
const cacheKey = (lang) => `ui_strings_${lang}`;

function readCache(lang) {
  if (lang === DEFAULT_LANG) return {};
  try {
    return JSON.parse(localStorage.getItem(cacheKey(lang)) || "{}");
  } catch (_e) {
    return {};
  }
}

const interpolate = (s, vars) =>
  vars ? s.replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined ? vars[k] : m)) : s;

export function I18nProvider({ children }) {
  const { pathname } = useLocation();
  const { lang } = splitLangPath(pathname);
  setCurrentLang(lang);
  const [store, setStore] = useState(() => ({ lang, strings: readCache(lang) }));
  const strings = store.lang === lang ? store.strings : readCache(lang);

  useEffect(() => {
    document.documentElement.lang = lang;
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/pages/ui", { params: { lang } });
        if (cancel) return;
        setStore({ lang, strings: data.strings || {} });
        if (lang !== DEFAULT_LANG) localStorage.setItem(cacheKey(lang), JSON.stringify(data.strings || {}));
      } catch (_e) { /* keep defaults */ }
    })();
    return () => {
      cancel = true;
    };
  }, [lang]);

  const value = useMemo(
    () => ({
      lang,
      t: (key, vars) => interpolate(strings[key] ?? UI_EN[key] ?? key, vars),
      localePath: (to) => localizePath(to, lang),
    }),
    [lang, strings]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);
